import createBreakpoints from "@material-ui/core/styles/createBreakpoints";
import createPalette from "@material-ui/core/styles/createPalette";
import createTypography from "@material-ui/core/styles/createTypography";
import { Overrides } from "@material-ui/core/styles/overrides";
import { Skin } from "themes/model";
import { createCustomTheme } from "./createCustomTheme";

const logoPath =
  "https://ringconfigimg.blob.core.windows.net/skins/Arnold/logo2.png";

const headerPath =
  "https://ringconfigimg.blob.core.windows.net/skins/Arnold/bg2.jpg";

export const palette = createPalette({
  common: {
    white: "#FFFFFF",
    black: "#333333",
  },
  primary: {
    light: "#EFF8F8",
    main: "#66C1BF",
  },
  text: {
    primary: "#222222",
    secondary: "#666666",
  },
  error: {
    light: "#FF5833",
    main: "#E43A15",
    dark: "#D02601",
  },
  warning: {
    light: "#FF893E",
    main: "#EF6B20",
    dark: "#DB570C",
  },
  info: {
    light: "#1E97FD",
    main: "#0079DF",
    dark: "#0065CB",
  },
  success: {
    light: "#1EC669",
    main: "#00A84B",
    dark: "#009437",
  },
  background: {
    default: "#ffffff",
  },
  grey: {
    100: "#F7F7F7",
    200: "#EEEEEE",
    300: "#E7E7E7",
    400: "#E4E2E3",
    500: "#B2B2B2",
    600: "#8C8C8C",
    700: "#666666",
    800: "#333333",
    900: "#1F1F1F",
  },
  divider: "#E4E2E3",
});

const fontFamily = "'Granville', sans-serif";

const weightNormal = 400;
const weightBold = 700;

export const typography = createTypography(palette, {
  h1: {
    fontStyle: "normal",
    fontWeight: weightNormal,
    fontFamily,
    fontSize: 33,
    lineHeight: 1,
    letterSpacing: 0,
  },
  h2: {
    fontStyle: "normal",
    fontWeight: 700,
    fontFamily,
    fontSize: "26px",
    lineHeight: 1,
    letterSpacing: 0,
  },
  h3: {
    fontStyle: "normal",
    fontWeight: weightNormal,
    fontFamily,
    fontSize: "20px",
    lineHeight: 1,
    letterSpacing: 0,
  },
  h4: {
    fontFamily,
    fontStyle: "normal",
    fontSize: "18px",
    lineHeight: 1,
    fontWeight: weightNormal,
    letterSpacing: 0,
  },
  h5: {
    fontFamily,
    fontStyle: "normal",
    fontSize: "16px",
    lineHeight: 1.2,
    fontWeight: weightBold,
    letterSpacing: 0,
  },
  h6: {
    fontFamily,
    fontStyle: "normal",
    fontSize: "16px",
    fontWeight: weightBold,
    lineHeight: 1,
    letterSpacing: 0,
  },
  subtitle1: {
    fontFamily,
    fontStyle: "normal",
    fontSize: "12px",
    lineHeight: 1.2,
    fontWeight: weightNormal,
    letterSpacing: 0,
  },
  subtitle2: {
    fontFamily,
    fontStyle: "normal",
    fontSize: "22px",
    lineHeight: 1.2,
    fontWeight: weightNormal,
    letterSpacing: 0,
    textTransform: "uppercase" as "uppercase",
  },
  button: {
    fontFamily,
    fontStyle: "normal",
    fontSize: "16px",
    fontWeight: weightBold,
    lineHeight: 1,
    letterSpacing: "1px",
  },
  caption: {
    fontFamily,
    fontSize: "12px",
    fontWeight: weightNormal,
    lineHeight: 1.4,
    letterSpacing: 0,
  },
  body1: {
    fontFamily,
    fontSize: "16px",
    fontWeight: weightNormal,
    lineHeight: 1.4,
    letterSpacing: 0,
  },
  body2: {
    fontFamily,
    fontSize: "12px",
    fontWeight: weightNormal,
    lineHeight: 1.3,
    letterSpacing: 0,
    "-webkit-font-smoothing": "subpixel-antialiased",
  },
});

const shape = {
  borderRadius: 0,
};

const breakpoints = createBreakpoints({
  values: {
    xs: 0,
    sm: 600,
    md: 960,
    lg: 1206,
    xl: 1920,
  },
});

const overrides: Overrides = {
  MuiCssBaseline: {
    "@global": {
      "@font-face": [
        {
          fontFamily: "Granville",
          fontWeight: 400,
          fontDisplay: "auto",
          fontStyle: "normal",
          src: `url("https://use.typekit.net/af/ae9c08/00000000000000007735bffb/30/l?primer=cd89bfea83225e29c3dc15f871708a0cb21f4a86ba808412c9c6f637938e51bb&fvd=n4&v=3") format("woff2"),url("https://use.typekit.net/af/ae9c08/00000000000000007735bffb/30/d?primer=cd89bfea83225e29c3dc15f871708a0cb21f4a86ba808412c9c6f637938e51bb&fvd=n4&v=3") format("woff"),url("https://use.typekit.net/af/ae9c08/00000000000000007735bffb/30/a?primer=cd89bfea83225e29c3dc15f871708a0cb21f4a86ba808412c9c6f637938e51bb&fvd=n4&v=3") format("opentype")`,
        },
        {
          fontFamily: "Granville",
          fontWeight: 700,
          fontDisplay: "auto",
          fontStyle: "normal",
          src: `url("https://use.typekit.net/af/ca35d2/00000000000000007735bfff/30/l?primer=cd89bfea83225e29c3dc15f871708a0cb21f4a86ba808412c9c6f637938e51bb&fvd=n7&v=3") format("woff2"),url("https://use.typekit.net/af/ca35d2/00000000000000007735bfff/30/d?primer=cd89bfea83225e29c3dc15f871708a0cb21f4a86ba808412c9c6f637938e51bb&fvd=n7&v=3") format("woff"),url("https://use.typekit.net/af/ca35d2/00000000000000007735bfff/30/a?primer=cd89bfea83225e29c3dc15f871708a0cb21f4a86ba808412c9c6f637938e51bb&fvd=n7&v=3") format("opentype")`,
        },
      ],
      ".MuiTypography-colorTextSecondary":
      {
        color: "#66C1BF",
      },
      ".value-icon":
      {
        height: ".75em"
      },
    },
  },
  MuiAppBar: {
    colorDefault: { 
      backgroundColor: "#FFFFFF",
    },
  },
};

export const skin: Skin = {
  logoPath,
  headerPath,
  headerHeight: 300,
  theme: createCustomTheme(palette, typography, shape, breakpoints, overrides),
};
